import React from 'react';

// useKeyState - keyboard events as values ( ¿ )

var EventEmitter = function EventEmitter() {
  this.events = {};
};

EventEmitter.prototype._getEventListByName = function _getEventListByName (eventName) {
  if (typeof this.events[eventName] === 'undefined') {
    this.events[eventName] = new Set();
  }

  return this.events[eventName];
};

EventEmitter.prototype.on = function on (eventName, fn) {
  this._getEventListByName(eventName).add(fn);
};

EventEmitter.prototype.once = function once (eventName, fn) {
  var self = this;

  var onceFn = function () {
      var args = [], len = arguments.length;
      while ( len-- ) args[ len ] = arguments[ len ];

    self.removeListener(eventName, onceFn);
    fn.apply(self, args);
  };

  this.on(eventName, onceFn);
};

EventEmitter.prototype.emit = function emit (eventName) {
    var args = [], len = arguments.length - 1;
    while ( len-- > 0 ) args[ len ] = arguments[ len + 1 ];
  [].concat( Array.from(this._getEventListByName(eventName)) ).reverse().forEach(function (fn) {
    fn.apply(this, args);
  }.bind(this));
};

EventEmitter.prototype.removeListener = function removeListener (eventName, fn) {
  this._getEventListByName(eventName).delete(fn);
}; // Document Event Listener


var eventEmitter = new EventEmitter();
var boundEvents = {};

function emitDomEvent(event) {
  eventEmitter.emit(event.type, event);
}

var DocumentEventListener = {
  addEventListener: function addEventListener(eventName, listener) {
    if (!boundEvents[eventName]) {
      document.addEventListener(eventName, emitDomEvent, true);
    }

    eventEmitter.on(eventName, listener);
  },

  removeEventListener: function removeEventListener(eventName, listener) {
    eventEmitter.removeListener(eventName, listener);
  }

}; // Key State

function KeyState(isDown, justReset) {
  if ( isDown === void 0 ) isDown = false;
  if ( justReset === void 0 ) justReset = false;

  this.pressed = isDown; //current (live) combo pressed state

  this.down = isDown; //only true for one read after combo becomes valid

  this.up = justReset; //only true for one read after combo is no longer valid
}

function _get(context, key) {
  var expiredKey = "_" + key + "Expired";
  var valueKey = "_" + key;

  if (context[expiredKey]) {
    return false;
  }

  context[expiredKey] = true;
  return context[valueKey];
}

function _set(context, key, value) {
  var expiredKey = "_" + key + "Expired";
  var valueKey = "_" + key;
  context[expiredKey] = false;
  context[valueKey] = value;
}

Object.defineProperty(KeyState.prototype, 'down', {
  get: function () {
    return _get(this, 'down');
  },
  set: function (value) {
    return _set(this, 'down', value);
  }
});
Object.defineProperty(KeyState.prototype, 'up', {
  get: function () {
    return _get(this, 'up');
  },
  set: function (value) {
    return _set(this, 'up', value);
  }
}); // Utils
// toCodes: maps our string notation to possible key event codes
// See: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
// Returns an array because browser / platform inconsistencies and we don't care to
// distinguish between left and right keys usually. If we can't map it we pass
// it through which means everyone is free to use the actual codes in the rules
// if they need more.

function toCodes(input) {
  if (!input.length) {
    return [];
  }

  var str = input.toLowerCase();
  var len = str.length;

  if (len === 1 && str.match(/[a-z]/i)) {
    // a-z
    return [("Key" + (str.toUpperCase()))];
  } else if (len === 1 && str.match(/[0-9]/i)) {
    // 0-9
    return [("Digit" + str)];
  }

  switch (str) {
    case '[':
      return ['BracketLeft'];

    case ']':
      return ['BracketRight'];

    case '\\':
    case 'backslash':
      return ['Backslash'];

    case 'period':
    case '.':
      return ['Period'];

    case 'comma':
    case ',':
      return ['Comma', 'NumpadComma'];

    case 'slash':
    case '/':
      return ['Slash'];

    case 'backquote':
    case '`':
      return ['Backquote'];

    case 'delete':
    case 'backspace':
      return ['Backspace', 'Delete'];

    case 'tab':
      return ['Tab'];

    case 'enter':
    case 'return':
      return ['Enter', 'NumpadEnter'];

    case 'shift':
      return ['ShiftLeft', 'ShiftRight'];

    case 'ctrl':
    case 'cntrl':
    case 'control':
      return ['ControlRight', 'ControlLeft'];

    case 'option':
    case 'opt':
    case 'alt':
      return ['AltLeft', 'AltRight'];

    case 'esc':
    case 'escape':
      return ['Escape'];

    case 'space':
      return ['Space'];

    case 'left':
      return ['ArrowLeft'];

    case 'up':
      return ['ArrowUp'];

    case 'right':
      return ['ArrowRight'];

    case 'down':
      return ['ArrowDown'];

    case 'cmd':
    case 'command':
    case 'win':
    case 'meta':
      return ['OSLeft', 'OSRight', 'MetaLeft', 'MetaRight'];

    case 'plus':
    case 'equal':
    case 'equals':
    case '=':
      return ['Equal'];

    case 'minus':
      return ['Minus'];

    case 'f1':
    case 'f2':
    case 'f3':
    case 'f4':
    case 'f5':
    case 'f6':
    case 'f8':
    case 'f9':
    case 'f10':
    case 'f11':
    case 'f12':
      return [str.toUpperCase()];

    default:
      // Pass input through to allow using specific event codes directly:
      return [input];
  }
}

function matchRule(rule, isDown) {
  function matchRuleStr(ruleStr) {
    var parts = parseRuleStr(ruleStr);
    var results = parts.map(function (str) { return isDown(toCodes(str)); });
    return results.every(function (r) { return r === true; });
  }

  if (Array.isArray(rule)) {
    return rule.some(function (ruleStr) { return matchRuleStr(ruleStr, isDown) === true; });
  }

  return matchRuleStr(rule, isDown);
}

function extractCaptureSet(rulesMap) {
  var captureSet = new Set();
  Object.entries(rulesMap).forEach(function (ref) {
    var value = ref[1];

    var rules = Array.isArray(value) ? value : [value];
    rules.forEach(function (rule) {
      var parts = parseRuleStr(rule);
      parts.forEach(function (part) {
        toCodes(part).forEach(function (code) { return captureSet.add(code); });
      });
    });
  });
  return captureSet;
}

function parseRuleStr(rule) {
  return rule.split('+').map(function (str) { return str.trim(); });
}

function mapRulesToState(rulesMap, prevState, isDown) {
  if ( prevState === void 0 ) prevState = {};
  if ( isDown === void 0 ) isDown = function () { return false; };

  var keysToState = Object.assign({}, prevState);
  Object.entries(rulesMap).forEach(function (ref) {
    var key = ref[0];
    var rule = ref[1];

    var matched = matchRule(rule, isDown);
    var prevKeyState = keysToState[key];

    if (prevKeyState) {
      if (prevKeyState.pressed !== matched) {
        var up = prevKeyState.pressed && !matched;
        keysToState[key] = new KeyState(matched, up);
      }
    } else {
      keysToState[key] = new KeyState();
    }
  });
  return keysToState;
}

function validateRulesMap(map) {
  // Expecting an object
  if (!map || typeof map !== 'object') {
    throw new Error("useKeyState: expecting an object {key:value<String|Array>} as first parameter.");
  } // Expecting string or array values for each key


  Object.entries(map).forEach(function (ref) {
    var key = ref[0];
    var value = ref[1];

    var isArray = Array.isArray(value);
    var isString = typeof value === 'string';

    if (!isString && !isArray) {
      throw new Error(("useKeyState: expecting string or array value for key " + key + "."));
    }

    if (isArray) {
      value.forEach(function (rule) {
        if (typeof rule !== 'string') {
          throw new Error(("useKeyState: expecting array of strings for key " + key));
        }
      });
    }
  });
} // Utils


function deepEqual(o1, o2) {
  return JSON.stringify(o1) === JSON.stringify(o2);
}

function isInputAcceptingTarget(event) {
  // content editable
  if (event.target.isContentEditable) {
    return true;
  } // form elements


  var tagName = (event.target || event.srcElement).tagName;
  return tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA';
} // Config


var defaultConfig = {
  captureEvents: false,
  // call event.preventDefault()
  ignoreRepeatEvents: true,
  // filter out repeat key events (whos event.repeat property is true)
  ignoreCapturedEvents: true,
  // respect the defaultPrevented event flag
  ignoreInputAcceptingElements: true,
  // filter out events from all forms of inputs
  debug: false
}; // useKeyState ¿

var useKeyState = function (rulesMap, configOverrides) {
  if ( rulesMap === void 0 ) rulesMap = {};
  if ( configOverrides === void 0 ) configOverrides = {};

  var configRef = React.useRef(Object.assign({}, defaultConfig,
    configOverrides));
  React.useEffect(function () {
    // configOverrides is likely to always be different:
    if (!deepEqual(configOverrides, configRef.current)) {
      configRef.current = Object.assign({}, defaultConfig,
        configOverrides);
    }
  }, [configOverrides]); // Maintain a copy of the rules map passed in

  var rulesMapRef = React.useRef({}); // Validate and update rulesMap when it changes to enable dynamic rules

  React.useEffect(function () {
    // rulesMap is likely to always be different:
    if (!deepEqual(rulesMap, rulesMapRef.current)) {
      validateRulesMap(rulesMap);
      rulesMapRef.current = rulesMap;
    }
  }, [rulesMap]); // Keep track of what keys are down

  var keyMapRef = React.useRef({}); // This gets passed back to the caller and is updated
  // once any hotkey rule matches or stops matching

  var ref = React.useState(function () { return mapRulesToState(rulesMap); });
  var state = ref[0];
  var setState = ref[1]; // Query live key state and some common key utility fns:
  // This object gets merged into return object

  var keyStateQuery = {
    pressed: function (input) {
      return matchRule(input, isDown);
    },
    space: function () {
      return isDown(toCodes('space'));
    },
    shift: function () {
      return isDown(toCodes('shift'));
    },
    ctrl: function () {
      return isDown(toCodes('ctrl'));
    },
    alt: function () {
      return isDown(toCodes('alt'));
    },
    option: function () {
      return isDown(toCodes('option'));
    },
    meta: function () {
      return isDown(toCodes('meta'));
    },
    esc: function () {
      return isDown(toCodes('esc'));
    }
  }; // Re-render the component if the key states have changed.
  // Must capture state value in a ref because the actual
  // updateKeyState function captures the initial value:

  var stateRef = React.useRef(state);
  React.useLayoutEffect(function () {
    stateRef.current = state;
  });
  var updateKeyState = React.useCallback(function () {
    var nextState = mapRulesToState(rulesMapRef.current, stateRef.current, isDown);
    var isEquivalentState = deepEqual(stateRef.current, nextState);

    if (configRef.current.debug) {
      console.log('useKeyState: rulesMap', Object.assign({}, rulesMapRef.current), 'keyMap', Object.assign({}, keyMapRef.current));
    }

    if (!isEquivalentState) {
      setState(nextState);
    }
  }, []);

  function isDown(codes) {
    var results = codes.map(function (code) { return keyMapRef.current[code] || false; });
    return results.some(function (r) { return r === true; });
  } // Event handlers


  var handleUp = React.useCallback(function (event) {
    if (configRef.current.debug) {
      console.log('useKeyState: up', event.code);
    } // Ignore events from input accepting elements (inputs etc)


    if (configRef.current.ignoreInputAcceptingElements && isInputAcceptingTarget(event)) {
      if (configRef.current.debug) {
        console.log('useKeyState: Ignoring captured up event:', event.code);
      }

      return;
    } // If Meta goes up, throw everything away because we might have stuck
    // keys


    if (toCodes('meta').includes(event.code)) {
      keyMapRef.current = {};
    }

    delete keyMapRef.current[event.code];
    updateKeyState();
  }, [updateKeyState]);
  var handleDown = React.useCallback(function (event) {
    if (configRef.current.debug) {
      console.log('useKeyState: down', event.code);
    } // Ignore events from input accepting elements (inputs etc)


    if (configRef.current.ignoreInputAcceptingElements && isInputAcceptingTarget(event)) {
      if (configRef.current.debug) {
        console.log('useKeyState: Ignoring event from input accepting element:', event.code);
      }

      return;
    } // Ignore handled event


    if (event.defaultPrevented && configRef.current.ignoreCapturedEvents) {
      if (configRef.current.debug) {
        console.log('useKeyState: Ignoring captured up event:', event.code);
      }

      return;
    } // Capture event if it is part of our rules and hook is configured to do so:


    if (configRef.current.captureEvents) {
      var captureSet = extractCaptureSet(rulesMapRef.current);

      if (captureSet.has(event.code)) {
        event.preventDefault();
      }
    } // Handle key repeat


    if (configRef.current.ignoreRepeatEvents === false && event.repeat && keyMapRef.current[event.code]) {
      // handle it as a key up (drop every other frame, hack)
      handleUp(event);
      return;
    } // Handle key that didn't receive a key up event - happens whe meta or ctrl is down


    if (keyMapRef.current[event.code]) {
      delete keyMapRef.current[event.code];
      updateKeyState();
    }

    keyMapRef.current[event.code] = true;
    updateKeyState();
  }, [handleUp, updateKeyState]);
  React.useEffect(function () {
    DocumentEventListener.addEventListener('keydown', handleDown);
    DocumentEventListener.addEventListener('keyup', handleUp);
    return function () {
      DocumentEventListener.removeEventListener('keydown', handleDown);
      DocumentEventListener.removeEventListener('keyup', handleUp);
    };
  }, [handleDown, handleUp]);
  return Object.assign({}, state,
    {keyStateQuery: keyStateQuery});
};

var index = {
  useKeyState: useKeyState
};

export default index;
